import React, { useState, useEffect } from 'react';
import { getNotificationPreferences, updateNotificationPreferences, getNotificationConfig, sendTestEmail } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Bell, Mail, Clock, AlertTriangle, Shield, FileText, GraduationCap, DollarSign, Loader2, Send, CheckCircle } from 'lucide-react';

const CATEGORY_ICONS = {
  incidents: AlertTriangle,
  assessments: Shield,
  renewals: Clock,
  claims: DollarSign,
  alerts: Bell,
  training: GraduationCap,
  policies: FileText,
};

const CATEGORY_LABELS = {
  incidents: 'Incidents',
  assessments: 'Assessments',
  renewals: 'Renewals',
  claims: 'Claims',
  alerts: 'Alerts',
  training: 'Training',
  policies: 'Safety Policies',
};

const CATEGORY_DESCRIPTIONS = {
  incidents: 'New incidents and high-severity alerts',
  assessments: 'Assessment completions and results',
  renewals: 'Renewal windows and upcoming deadlines',
  claims: 'New claims and status updates',
  alerts: 'Risk alerts and score changes',
  training: 'Training due dates and overdue notices',
  policies: 'Policy adoption requests and completions',
};

export default function NotificationPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [config, setConfig] = useState(null);
  const [preferences, setPreferences] = useState({
    digest_frequency: 'weekly',
    categories: {
      incidents: true,
      assessments: true,
      renewals: true,
      claims: true,
      alerts: true,
      training: true,
      policies: true,
    },
    followed_orgs: [],
    email_enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, prefsRes] = await Promise.all([
        getNotificationConfig(),
        getNotificationPreferences(),
      ]);
      setConfig(configRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificationPreferences(preferences);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setSendingTest(true);
    setTestEmailSent(false);
    try {
      const response = await sendTestEmail();
      toast.success(response.data.message || 'Test email sent successfully!');
      setTestEmailSent(true);
      // Reset the success state after 5 seconds
      setTimeout(() => setTestEmailSent(false), 5000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send test email';
      toast.error(errorMessage);
    } finally {
      setSendingTest(false);
    }
  };

  const handleCategoryToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category]
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Status Banner */}
      <Card className={`border ${config?.email_configured ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
        <CardContent className="p-4 flex items-center gap-3">
          <Mail className={`w-5 h-5 ${config?.email_configured ? 'text-green-500' : 'text-amber-500'}`} />
          <div className="flex-1">
            <p className="font-medium">
              {config?.email_configured ? 'Email notifications are enabled' : 'Email service not configured'}
            </p>
            <p className="text-sm text-muted-foreground">
              {config?.email_configured 
                ? 'You will receive email notifications based on your preferences below'
                : 'Contact your administrator to enable email notifications'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {config?.email_configured && (
              <Button
                variant={testEmailSent ? "outline" : "secondary"}
                size="sm"
                onClick={handleTestEmail}
                disabled={sendingTest || testEmailSent}
                data-testid="send-test-email-btn"
                className={testEmailSent ? "border-green-500 text-green-500" : ""}
              >
                {sendingTest ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : testEmailSent ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Sent!
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            )}
            <Badge variant={config?.email_configured ? 'default' : 'secondary'}>
              {config?.email_configured ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Main Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Email Notification Settings
          </CardTitle>
          <CardDescription>
            Control how and when you receive email notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable All */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div>
              <Label className="text-base font-medium">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Enable or disable all email notifications
              </p>
            </div>
            <Switch
              checked={preferences.email_enabled}
              onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_enabled: checked }))}
              data-testid="email-enabled-switch"
            />
          </div>

          {/* Digest Frequency */}
          <div className="space-y-2">
            <Label>Summary Digest Frequency</Label>
            <p className="text-sm text-muted-foreground mb-2">
              How often to receive summary digests (for National/State admins)
            </p>
            <Select
              value={preferences.digest_frequency}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, digest_frequency: value }))}
              disabled={!preferences.email_enabled}
            >
              <SelectTrigger className="w-[200px]" data-testid="digest-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Toggles */}
          <div className="space-y-3">
            <Label>Notification Categories</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Select which types of notifications you want to receive
            </p>
            <div className="grid gap-3">
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                const Icon = CATEGORY_ICONS[key];
                return (
                  <div
                    key={key}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      preferences.categories[key] 
                        ? 'border-primary/30 bg-primary/5' 
                        : 'border-border bg-secondary/20'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        preferences.categories[key] ? 'bg-primary/10' : 'bg-secondary'
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          preferences.categories[key] ? 'text-primary' : 'text-muted-foreground'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{label}</p>
                        <p className="text-xs text-muted-foreground">{CATEGORY_DESCRIPTIONS[key]}</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.categories[key]}
                      onCheckedChange={() => handleCategoryToggle(key)}
                      disabled={!preferences.email_enabled}
                      data-testid={`category-${key}-switch`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} data-testid="save-preferences-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin Level Info */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-base">How Notifications Work</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-3">
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="mt-0.5">Local</Badge>
            <p>Local admins receive detailed, real-time notifications for events in their organization only.</p>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="mt-0.5">State</Badge>
            <p>State admins receive summary notifications for organizations within their state.</p>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="mt-0.5">National</Badge>
            <p>National admins receive digest summaries only - no detailed local event emails to prevent notification overload.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
